import { Component, Injector, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, EventEmitter, Output, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { SelectionItem } from '../../../core/component/select/select.component';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { FakeGuidService } from '../../../core/service/fake-guid.service';
import { RoomType, RoomTypeToString } from '../../../dwelling-inspection/model/dwelling-inspection-room';
import { Dwelling } from '../../model/dwelling';
import { ObjectComponent } from '../../model/object-component';
import { ObjectComponentCategory } from '../../model/object-component-category';
import { Premises } from '../../model/premises';
import { Room } from '../../model/room';
import { ObjectComponentCategoryService } from '../../service/data/object-component-category.service';
import { ObjectComponentService } from '../../service/data/object-component.service';

@Component({
    selector: 'app-room-form',
    templateUrl: './room-form.component.html',
    styleUrls: ['./room-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RoomFormComponent implements OnInit, OnDestroy, OnChanges {
  constructor(
    private _componentCategoryService: ObjectComponentCategoryService,
    private _componentService: ObjectComponentService,
    private _cf: FormControlExFactory,
    private _fb: FormBuilder,
    private _injector: Injector,
    private _actionWindow: ActionWindowService
  ) {}

  @Input() componentCategories: ObjectComponentCategory[];
  @Input() rootForm: FormGroup;
  private _forms: FormArray;
  @Input() set forms(value: AbstractControl) {
    this._forms = value as FormArray;
  }
  get forms(): FormArray {
    return this._forms;
  }
  @Input() room: Room;
  @Input() rentalObject: Dwelling | Premises;
  @Output() componentAdded = new EventEmitter<void>();

  get rentalObjectId(): string {
    return this.rentalObject.id;
  }
  private _subscription = new Subscription();
  form: FormGroup;

  icons: any;
  roomTypes: SelectionItem[] = [
    new SelectionItem(RoomTypeToString(RoomType.Bedroom), RoomType.Bedroom),
    new SelectionItem(RoomTypeToString(RoomType.Bathroom), RoomType.Bathroom),
    new SelectionItem(RoomTypeToString(RoomType.DressingRoom), RoomType.DressingRoom),
    new SelectionItem(RoomTypeToString(RoomType.Hall), RoomType.Hall),
    new SelectionItem(RoomTypeToString(RoomType.Kitchen), RoomType.Kitchen),
    new SelectionItem(RoomTypeToString(RoomType.LivingRoom), RoomType.LivingRoom),
    new SelectionItem(RoomTypeToString(RoomType.Toilet), RoomType.Toilet),
    new SelectionItem(RoomTypeToString(RoomType.Balcony), RoomType.Balcony),
    new SelectionItem(RoomTypeToString(RoomType.Patio), RoomType.Patio),
    new SelectionItem(RoomTypeToString(RoomType.Other), RoomType.Other),
  ];

  addComponent() {
    this.room.components.push(
      new ObjectComponent({
        componentCategory: this.componentCategories[0].id,
        componentType: this.componentCategories[0].objectComponentTypes[0].id,
      })
    );
    this.forms.markAsDirty();
    this.rootForm.markAsDirty(); // moved it here, as it was always dirty before. Might be another way to mark dirty but leaving it like this for current scope
    this.componentAdded.emit();
  }

  delete() {
    // new item has no id so we search by inspectionOrder field
    const roomIndex = this.rentalObject.rooms
      .findIndex(r => r.inspectionOrder === this.room.inspectionOrder);
    if (roomIndex === -1) return; // impossible, but...

    // delete item from array and form
    this.rentalObject.rooms.splice(roomIndex, 1);
    this.forms.removeAt(roomIndex);

    // execute items reordering
    this.rentalObject.rooms.forEach((room, index) => {
      const newOrder = index + 1; // order starts from 1, so we increment index
      room.inspectionOrder = newOrder;
      const fg = this.forms.at(index) as FormGroup;
      fg.controls['inspectionOrder'].setValue(newOrder);
    });

    this.rootForm.markAsDirty();
  }

  ngOnDestroy(): void {
    if (this._subscription) this._subscription.unsubscribe();
  }

  ngOnInit(): void {
    this.form = this.createRoomForm(this.room);
    this.forms.controls.push(this.form);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['room']) {
      this.icons = {
        'balcony-i': this.room.type === RoomType.Balcony,
        'bedroom-i': this.room.type === RoomType.Bedroom,
        'bathroom-i': this.room.type === RoomType.Bathroom,
        'dressing-room-i': this.room.type === RoomType.DressingRoom,
        'hall-i': this.room.type === RoomType.Hall,
        'kitchen-i': this.room.type === RoomType.Kitchen,
        'living-room-i': this.room.type === RoomType.LivingRoom,
        'room-i': true,
        'toilet-i': this.room.type === RoomType.Toilet,
        'other-room-i': this.room.type === RoomType.Other,
      };
    }
  }

  private createRoomForm(room: Room): FormGroup {
    let form = this._fb.group({
      id: this._cf.createHidden(room.id),
      type: this._cf.createMandatory('DWELLING_INSPECTION.ROOM_TYPE', room.type),
      inspectionOrder: this._cf.createMandatory('DWELLING_INSPECTION.INSPECTION_ORDER', room.inspectionOrder),
      caption: this._cf.createMandatory('COMMON.CAPTION', room.caption),
      area: this._cf.create('COMMON.AREA', room.area),
      components: this._fb.array([]),
    });
    this._subscription.add(form.valueChanges.subscribe(x => this.rootForm.markAsDirty()));

    form.controls['type'].valueChanges.subscribe(val => {
      this.room.type = val;
      this.icons = {
        'bedroom-i': this.room.type === RoomType.Bedroom,
        'bathroom-i': this.room.type === RoomType.Bathroom,
        'dressing-room-i': this.room.type === RoomType.DressingRoom,
        'hall-i': this.room.type === RoomType.Hall,
        'kitchen-i': this.room.type === RoomType.Kitchen,
        'living-room-i': this.room.type === RoomType.LivingRoom || this.room.type === RoomType.Other,
        'room-i': true,
        'toilet-i': this.room.type === RoomType.Toilet,
      };
    });
    return form;
  }
}
